import axios from "utils/axios";
import { API_URL } from "configs";
import * as types from "./ActionTypes";

const showLoader = (label) => ({
  type: "SHOW_LOADER",
  common_loader_label: label,
});

const hideLoader = () => ({
  type: "HIDE_LOADER",
});

const showSnackbar = (message, notificationType = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: notificationType,
});

const authHeaders = () => ({
  "Content-Type": "application/json",
  Accept: "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const normalizeEnvelope = (res) => {
  const envelope = res?.data || {};
  return {
    success: envelope?.success,
    message: envelope?.message || "",
    data: envelope?.data ?? null,
    errors: envelope?.errors ?? null,
    meta: envelope?.meta ?? null,
  };
};

const extractArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload && typeof payload === "object") return [payload];
  return [];
};

const extractRecord = (payload) => {
  if (!payload) return null;
  if (payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)) {
    return payload.data;
  }
  if (Array.isArray(payload?.items) && payload.items.length > 0) {
    return payload.items[0];
  }
  if (payload && typeof payload === "object" && !Array.isArray(payload)) return payload;
  return null;
};

const ensureObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const pickFirstNonEmptyObject = (...candidates) => {
  for (const candidate of candidates) {
    const safeObject = ensureObject(candidate);
    if (Object.keys(safeObject).length > 0) {
      return safeObject;
    }
  }

  return {};
};

const splitLegacyTelecomSections = (sections = {}) => {
  const safeSections = ensureObject(sections);
  const legacy = ensureObject(safeSections.telecom);

  if (Object.keys(legacy).length === 0) {
    return safeSections;
  }

  return {
    ...safeSections,
    ...(safeSections.telecom_support === undefined
      ? {
          telecom_support: {
            title: legacy.title,
            licenseRate: legacy.licenseRate,
            numberOfLicenses:
              legacy.numberOfLicenses ??
              legacy.noOfLicenses ??
              legacy.noOfLicense,
          },
        }
      : {}),
    ...(safeSections.telecom_consumption === undefined
      ? {
          telecom_consumption: {
            rate: legacy.rate ?? legacy.perMinuteRate,
            chargeTimeType: legacy.chargeTimeType || legacy.charge_time_type,
            chargeTimeUnit: legacy.chargeTimeUnit || legacy.charge_time_unit,
            handleTime:
              legacy.handleTime ??
              legacy.handle_time ??
              legacy.acdHandleTime,
            talkTime:
              legacy.talkTime ?? legacy.talk_time ?? legacy.acdTalkTime,
            totalMinutes: legacy.totalMinutes ?? legacy.total_minutes,
          },
        }
      : {}),
  };
};

const normalizeInvoiceSections = (value, fallbackMonthKey = "") => {
  const safeValue = ensureObject(value);

  const directSections = ensureObject(safeValue?.sections);
  if (Object.keys(directSections).length > 0) {
    return directSections;
  }

  const invoiceNode = ensureObject(safeValue?.invoice);
  if (Object.keys(invoiceNode).length > 0) {
    return {
      invoice: {
        ...invoiceNode,
        monthKey: invoiceNode?.monthKey || fallbackMonthKey,
      },
    };
  }

  if (Array.isArray(safeValue?.rows) || safeValue?.grandTotal !== undefined) {
    return {
      invoice: {
        monthKey: safeValue?.monthKey || fallbackMonthKey,
        rows: Array.isArray(safeValue?.rows) ? safeValue.rows : [],
        grandTotal: safeValue?.grandTotal ?? 0,
      },
    };
  }

  return {};
};

const getInvoiceSectionsFromRecord = (record, fallbackMonthKey = "") => {
  const tabsInvoice = ensureObject(record?.tabs?.invoice);

  return pickFirstNonEmptyObject(
    tabsInvoice?.sections,
    normalizeInvoiceSections(tabsInvoice, fallbackMonthKey),
    normalizeInvoiceSections(record?.invoice, fallbackMonthKey)
  );
};

const normalizeTabNewFromRecord = (record) => {
  const tabs = ensureObject(record?.tabs);
  const directSections = ensureObject(record?.sections);
  const payloadSections = ensureObject(record?.payload?.sections);

  const sectionsSource =
    Object.keys(directSections).length > 0 ? directSections : payloadSections;

  const pickSections = (sectionIds = []) =>
    sectionIds.reduce((acc, sectionId) => {
      if (sectionsSource?.[sectionId] !== undefined) {
        acc[sectionId] = sectionsSource[sectionId];
      }
      return acc;
    }, {});

  const mergeWithFallback = (tabSections = {}, fallbackSectionIds = []) => {
    const safeTabSections = ensureObject(tabSections);
    if (Object.keys(safeTabSections).length > 0) {
      return safeTabSections;
    }

    return pickSections(fallbackSectionIds);
  };

  return {
    labor: mergeWithFallback(ensureObject(tabs?.labor?.sections), [
      "account_management",
      "supervisor",
      "supervisor_2",
      "rep_hours_onshore",
      "rep_hours_offshore",
      "crm_reporting_support",
      "it_support",
    ]),
    technology: splitLegacyTelecomSections(
      mergeWithFallback(ensureObject(tabs?.technology?.sections), [
        "crm_subscription_annual",
        "telecom_support",
        "telecom_consumption",
        "misc_telecom",
      ]),
    ),
    fulfilment: mergeWithFallback(ensureObject(tabs?.fulfilment?.sections), [
      "fulfillment",
      "fulfillment_misc",
      "fulfillment_tracker",
      "fulfillment_tracker_1",
      "supplies",
      "storage",
      "gift_cards",
      "fedex_expenses",
    ]),
    miscellaneous_expenses: mergeWithFallback(
      ensureObject(tabs?.miscellaneous_expenses?.sections),
      [
      "professional_voice_recording",
      "misc_telecom",
      "misc_credit",
      ]
    ),
    invoice: getInvoiceSectionsFromRecord(record),
  };
};

const createDirtyMap = (isDirty = false) => ({
  labor: Boolean(isDirty),
  technology: Boolean(isDirty),
  fulfilment: Boolean(isDirty),
  miscellaneous_expenses: Boolean(isDirty),
});

const TAB_SECTION_OPTIONS = {
  labor: [
    "account_management",
    "supervisor",
    "supervisor_2",
    "rep_hours_onshore",
    "rep_hours_offshore",
    "crm_reporting_support",
    "it_support",
  ],
  technology: [
    "crm_subscription_annual",
    "telecom_support",
    "telecom_consumption",
    "misc_telecom",
  ],
  fulfilment: [
    "fulfillment",
    "fulfillment_misc",
    "fulfillment_tracker",
    "fulfillment_tracker_1",
    "supplies",
    "storage",
    "gift_cards",
    "fedex_expenses",
  ],
  miscellaneous_expenses: [
    "professional_voice_recording",
    "misc_telecom",
    "misc_credit",
  ],
};

const buildSectionStatesForTab = (
  tabId,
  selectedSectionIds = [],
  allSectionIds = null,
) => {
  const allSections =
    Array.isArray(allSectionIds) && allSectionIds.length > 0
      ? allSectionIds
      : TAB_SECTION_OPTIONS?.[tabId] || [];

  return allSections.map((sectionId) => ({
    tab: tabId,
    section_id: sectionId,
    is_enabled: selectedSectionIds.includes(sectionId),
  }));
};

const handleApiFailure = (error, dispatch, fallbackMessage = "Request failed") => {
  const status = error?.response?.status;
  const payloadErrors = error?.response?.data?.errors;
  const apiMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    fallbackMessage;

  if (status === 409) {
    const readonlyMessage = "Invoice is read-only for this month.";
    dispatch(showSnackbar(readonlyMessage));

    return {
      validationErrors: null,
      message: readonlyMessage,
    };
  }

  if (status === 422) {
    return {
      validationErrors: payloadErrors || {},
      message: apiMessage || "Validation failed. Please review the highlighted fields.",
    };
  }

  dispatch(showSnackbar(apiMessage));

  return {
    validationErrors: null,
    message: apiMessage,
  };
};

const mergeTabIntoConfigRecord = (record, tabId, tabPayload) => {
  const source = ensureObject(record);
  const nextTabs = ensureObject(source.tabs);
  const tabEntry = ensureObject(nextTabs[tabId]);

  return {
    ...source,
    tabs: {
      ...nextTabs,
      [tabId]: {
        ...tabEntry,
        sections: {
          ...ensureObject(tabEntry.sections),
          ...ensureObject(tabPayload),
        },
      },
    },
  };
};

const toApiBoolean = (value, fallback = false) => {
  if (value === undefined || value === null || value === "") return fallback;
  return value === true || value === 1 || value === "1" || value === "true";
};

const toApiDecimal = (value, precision = 4) => {
  if (value === undefined || value === null) return value;

  const raw = String(value).trim();
  if (!raw) return raw;

  const normalized = raw.replace(/[$,%\s,]/g, "");
  const parsed = Number(normalized);

  if (!Number.isFinite(parsed)) return value;

  return Number(parsed.toFixed(precision));
};

const toApiDecimalMap = (value, precision = 4) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((acc, [key, raw]) => {
    const normalizedKey = String(key || "").trim();
    if (!normalizedKey) return acc;
    acc[normalizedKey] = toApiDecimal(raw, precision);
    return acc;
  }, {});
};

const toApiStringMap = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return Object.entries(value).reduce((acc, [key, raw]) => {
    const normalizedKey = String(key || "").trim();
    if (!normalizedKey) return acc;
    acc[normalizedKey] = raw === undefined || raw === null ? "" : String(raw);
    return acc;
  }, {});
};

const toApiDecimal2dp = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const numeric = toApiDecimal(value, 2);
  if (numeric === undefined || numeric === null || Number.isNaN(Number(numeric))) {
    return "";
  }
  return Number(numeric).toFixed(2);
};

const toApiDecimalString = (value, precision = 4) => {
  if (value === undefined || value === null || value === "") return "";
  const numeric = toApiDecimal(value, precision);
  if (numeric === undefined || numeric === null || Number.isNaN(Number(numeric))) {
    return "";
  }
  return String(numeric);
};

const toApiPostageRows = (value) => {
  if (!Array.isArray(value)) return [];

  return value
    .map((row) => {
      const source = ensureObject(row);
      const brandId = String(source?.brand_id || source?.brandId || "").trim();
      const brandName = String(
        source?.brand_name ||
          source?.brandName ||
          source?.brand ||
          source?.program_brand ||
          source?.programBrand ||
          ""
      ).trim();

      return {
        ...(brandId ? { brand_id: brandId } : {}),
        ...(!brandId && brandName ? { brand_name: brandName } : {}),
        ...(brandId && brandName ? { brand_name: brandName } : {}),
        items: toApiDecimalString(source?.items),
        postage_used: toApiDecimalString(source?.postage_used ?? source?.postageUsed),
      };
    })
    .filter((row) => Boolean(row?.brand_id || row?.brand_name));
};

const filterMonthlyByMonthKey = (monthlyValue, monthKey = "") => {
  const monthly = ensureObject(monthlyValue);
  const normalizedMonthKey = String(monthKey || "").trim();

  if (!normalizedMonthKey) {
    return monthly;
  }

  if (monthly?.[normalizedMonthKey] === undefined) {
    return {};
  }

  return {
    [normalizedMonthKey]: monthly[normalizedMonthKey],
  };
};

const toApiNegativeDecimalString = (value, precision = 2) => {
  if (value === undefined || value === null || value === "") return "";
  const numeric = toApiDecimal(value, precision);
  if (numeric === undefined || numeric === null || numeric === "" || Number.isNaN(Number(numeric))) {
    return "";
  }
  const parsed = Number(numeric);
  if (!Number.isFinite(parsed)) return "";
  if (parsed === 0) return (0).toFixed(precision);
  return (-Math.abs(parsed)).toFixed(precision);
};

const toApiWholeNonNegativeIntString = (value) => {
  if (value === undefined || value === null || value === "") return "";

  const raw = String(value).trim();
  if (!raw) return "";

  if (!/^(\d+)(\.\d+)?$/.test(raw)) return "";

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
    return "";
  }

  return String(parsed);
};

const sanitizeMonthlyWholeNonNegativeCountFields = (monthlyValue, fields = []) => {
  const monthly = ensureObject(monthlyValue);

  return Object.entries(monthly).reduce((acc, [monthKey, monthEntry]) => {
    const source = ensureObject(monthEntry);
    const normalizedEntry = { ...source };

    fields.forEach((field) => {
      if (Object.prototype.hasOwnProperty.call(source, field)) {
        normalizedEntry[field] = toApiWholeNonNegativeIntString(source[field]);
      }
    });

    acc[monthKey] = normalizedEntry;
    return acc;
  }, {});
};

const sanitizeInvoiceTabPayload = (tabId, rawPayload, options = {}) => {
  const shouldStripInvoiceOnlyFieldsForConfig = Boolean(options?.forConfigSync);
  const activeMonthKey = String(options?.monthKey || "").trim();
  const payload = ensureObject(rawPayload);

  if (tabId === "fulfilment") {
    const stripMonthly = (sectionValue) => {
      const section = ensureObject(sectionValue);
      const { monthly, ...fields } = section;
      return fields;
    };

    const keepSupportedFulfillmentFields = (sectionValue) => {
      const section = ensureObject(sectionValue);
      return {
        ...(section?.title !== undefined ? { title: section.title } : {}),
        ...(section?.postageTitle !== undefined
          ? { postageTitle: section.postageTitle }
          : {}),
        ...(section?.perPieceRate !== undefined ? { perPieceRate: section.perPieceRate } : {}),
        ...(section?.chargeBrandwise !== undefined
          ? { chargeBrandwise: Boolean(section.chargeBrandwise) }
          : {}),
        ...(section?.perPieceRateByBrand !== undefined
          ? { perPieceRateByBrand: toApiDecimalMap(section.perPieceRateByBrand) }
          : {}),
        ...(section?.pieceTitleByBrand !== undefined
          ? { pieceTitleByBrand: toApiStringMap(section.pieceTitleByBrand) }
          : {}),
        ...(section?.postageTitleByBrand !== undefined
          ? { postageTitleByBrand: toApiStringMap(section.postageTitleByBrand) }
          : {}),
        ...(section?.program_brands !== undefined
          ? {
              program_brands: Array.isArray(section.program_brands)
                ? section.program_brands.map((brand) => String(brand || "").trim()).filter(Boolean)
                : [],
            }
          : {}),
        ...(section?.postage_rows !== undefined
          ? { postage_rows: toApiPostageRows(section.postage_rows) }
          : {}),
      };
    };

    const keepSupportedTrackerFields = (sectionValue) => {
      const rawSection = ensureObject(sectionValue);
      const section = stripMonthly(sectionValue);
      const checkProcessingRate = toApiDecimal(section?.checkProcessingRate);
      const voidProcessingRate = toApiDecimal(section?.voidProcessingRate);
      const stoppedPaymentRate = toApiDecimal(section?.stoppedPaymentRate);
      const result = {
        ...(section?.checkProcessingTitle !== undefined
          ? { checkProcessingTitle: section.checkProcessingTitle }
          : {}),
        ...(section?.refundChecksIssuedTitle !== undefined
          ? { refundChecksIssuedTitle: section.refundChecksIssuedTitle }
          : {}),
        ...(section?.checkProcessingRate !== undefined
          ? { checkProcessingRate }
          : {}),
        ...(section?.voidProcessingTitle !== undefined
          ? { voidProcessingTitle: section.voidProcessingTitle }
          : {}),
        ...(section?.voidProcessingRate !== undefined
          ? { voidProcessingRate }
          : {}),
        ...(section?.stoppedPaymentTitle !== undefined
          ? { stoppedPaymentTitle: section.stoppedPaymentTitle }
          : {}),
        ...(section?.stoppedPaymentRate !== undefined
          ? { stoppedPaymentRate }
          : {}),
      };

      if (!shouldStripInvoiceOnlyFieldsForConfig) {
        const monthly = sanitizeMonthlyWholeNonNegativeCountFields(
          filterMonthlyByMonthKey(rawSection?.monthly, activeMonthKey),
          ["checkCount", "voidCount"]
        );
        result.monthly = Object.entries(monthly).reduce((acc, [monthKey, monthEntry]) => {
          const source = ensureObject(monthEntry);
          acc[monthKey] = {
            ...source,
            ...(Object.prototype.hasOwnProperty.call(source, "voidAmount")
              ? { voidAmount: toApiNegativeDecimalString(source.voidAmount) }
              : {}),
          };
          return acc;
        }, {});
      }

      return result;
    };

    const keepSupportedTracker1Fields = (sectionValue) => {
      const rawSection = ensureObject(sectionValue);
      const section = stripMonthly(sectionValue);
      const retrievalHoursRate = toApiDecimal(section?.retrievalHoursRate);
      const retrievalRate = toApiDecimal(section?.retrievalRate);
      const nonRetrievalRate = toApiDecimal(section?.nonRetrievalRate);
      const result = {
        ...(section?.retrievalTitle !== undefined
          ? { retrievalTitle: section.retrievalTitle }
          : {}),
        ...(section?.retrievalHoursRate !== undefined
          ? { retrievalHoursRate }
          : {}),
        ...(section?.retrievalRate !== undefined
          ? { retrievalRate }
          : {}),
        ...(section?.billRetrievalByHours !== undefined
          ? { billRetrievalByHours: Boolean(section.billRetrievalByHours) }
          : {}),
        ...(section?.nonRetrievalTitle !== undefined
          ? { nonRetrievalTitle: section.nonRetrievalTitle }
          : {}),
        ...(section?.nonRetrievalRate !== undefined
          ? { nonRetrievalRate }
          : {}),
        ...(section?.minutesPerRetrieval !== undefined
          ? { minutesPerRetrieval: section.minutesPerRetrieval }
          : {}),
      };

      if (!shouldStripInvoiceOnlyFieldsForConfig) {
        const monthly = sanitizeMonthlyWholeNonNegativeCountFields(
          filterMonthlyByMonthKey(rawSection?.monthly, activeMonthKey),
          ["retrievalCount", "nonRetrievalCount"]
        );
        result.monthly = monthly;
      }

      return result;
    };

    const toRowArray = (sectionValue) =>
      Array.isArray(sectionValue)
        ? sectionValue
        : sectionValue && typeof sectionValue === "object"
          ? [sectionValue]
          : [];

    const keepSupportedFulfillmentMiscRows = (sectionValue) => {
      const rows = toRowArray(sectionValue);

      return rows.map((row) => {
        const source = ensureObject(row);
        const next = {
          title: source.title === undefined || source.title === null ? "" : String(source.title),
          rate: toApiDecimal2dp(source.rate),
        };

        if (!shouldStripInvoiceOnlyFieldsForConfig) {
          const items = toApiWholeNonNegativeIntString(source.items);
          if (items !== "") {
            next.items = items;
          }
        }

        return next;
      });
    };

    const keepSupportedTitleChargeRows = (sectionValue) =>
      toRowArray(sectionValue).map((row) => {
        const source = ensureObject(row);
        const next = {
          title: source.title === undefined || source.title === null ? "" : String(source.title),
        };

        if (!shouldStripInvoiceOnlyFieldsForConfig) {
          const charge = toApiDecimal2dp(
            source.charge ?? source.miscellaneousSuppliesReceipts
          );
          if (charge !== "") {
            next.charge = charge;
          }
        }

        return next;
      });

    const keepSupportedGiftCardRows = (sectionValue) =>
      toRowArray(sectionValue).map((row) => {
        const source = ensureObject(row);
        const next = {
          title: source.title === undefined || source.title === null ? "" : String(source.title),
          processingFee: toApiDecimal2dp(source.processingFee),
        };

        if (!shouldStripInvoiceOnlyFieldsForConfig) {
          const numberProcessed = toApiWholeNonNegativeIntString(
            source.numberProcessed ?? source.numberOfGiftCardsOrdered
          );
          if (numberProcessed !== "") {
            next.numberProcessed = Number(numberProcessed);
          }
        }

        return next;
      });

    const keepSupportedFedexRows = (sectionValue) =>
      toRowArray(sectionValue).map((row) => {
        const source = ensureObject(row);
        return {
          title: source.title === undefined || source.title === null ? "" : String(source.title),
          baseCharge: toApiDecimal2dp(source.baseCharge),
        };
      });

    const result = {};

    if (payload?.fulfillment !== undefined) {
      result.fulfillment = keepSupportedFulfillmentFields(payload?.fulfillment);
    }

    if (payload?.fulfillment_tracker !== undefined) {
      result.fulfillment_tracker = keepSupportedTrackerFields(payload?.fulfillment_tracker);
    }

    if (payload?.fulfillment_tracker_1 !== undefined) {
      result.fulfillment_tracker_1 = keepSupportedTracker1Fields(payload?.fulfillment_tracker_1);
    }

    if (payload?.fulfillment_misc !== undefined) {
      result.fulfillment_misc = keepSupportedFulfillmentMiscRows(payload?.fulfillment_misc);
    }

    if (payload?.fedex_expenses !== undefined) {
      result.fedex_expenses = keepSupportedFedexRows(payload?.fedex_expenses);
    }

    if (payload?.supplies !== undefined) {
      result.supplies = keepSupportedTitleChargeRows(payload?.supplies);
    }

    if (payload?.storage !== undefined) {
      result.storage = keepSupportedTitleChargeRows(payload?.storage);
    }

    if (payload?.gift_cards !== undefined) {
      result.gift_cards = keepSupportedGiftCardRows(payload?.gift_cards);
    }

    return result;
  }

  if (tabId === "technology") {
    const result = { ...payload };

    if (payload?.crm_subscription_annual !== undefined) {
      const rows = Array.isArray(payload.crm_subscription_annual)
        ? payload.crm_subscription_annual
        : payload.crm_subscription_annual &&
            typeof payload.crm_subscription_annual === "object"
          ? [payload.crm_subscription_annual]
          : [];

      result.crm_subscription_annual = rows.map((row) => {
        const source = ensureObject(row);
        return {
          ...(source.subscriptionId
            ? { subscriptionId: String(source.subscriptionId) }
            : {}),
          title: source.title ?? "",
          crmSubscription: source.crmSubscription ?? "",
          crmDateFrom: source.crmDateFrom ?? "",
          crmDateTo: source.crmDateTo ?? "",
          isPaid:
            source.isPaid === true ||
            source.isPaid === 1 ||
            source.isPaid === "1" ||
            source.isPaid === "true",
        };
      });
    }

    if (payload?.telecom_consumption !== undefined) {
      const section = ensureObject(payload.telecom_consumption);
      if (shouldStripInvoiceOnlyFieldsForConfig) {
        const {
          handleTime,
          talkTime,
          totalMinutes,
          handle_time,
          talk_time,
          total_minutes,
          acdHandleTime,
          acdTalkTime,
          acdTotalMinutes,
          acd_handle_time,
          acd_talk_time,
          acd_total_minutes,
          ...allowedFields
        } = section;
        result.telecom_consumption = allowedFields;
      } else {
        result.telecom_consumption = section;
      }
    }

    return result;
  }

  if (tabId !== "labor") return payload;

  const sanitizeLaborTaskSection = (sectionId, sectionValue) => {
    const section = ensureObject(sectionValue);
    const isTaskSection = String(sectionId || "").startsWith("task_");
    const isFlatRate =
      section.isFlatRate === undefined
        ? true
        : toApiBoolean(section.isFlatRate, true);
    const chargeManual = toApiBoolean(section.chargeManual, false);

    if (shouldStripInvoiceOnlyFieldsForConfig) {
      const {
        actualAmHours,
        overageHours,
        chargeManual: _chargeManual,
        manualHours: _manualHours,
        ...allowedFields
      } = section;
      return allowedFields;
    }

    const next = { ...section, isFlatRate };

    if (section.actualAmHours !== undefined && section.actualAmHours !== null && section.actualAmHours !== "") {
      next.actualAmHours = String(section.actualAmHours);
    }

    if (!isTaskSection) {
      delete next.chargeManual;
      delete next.manualHours;
      return next;
    }

    if (isFlatRate) {
      delete next.chargeManual;
      delete next.manualHours;
      return next;
    }

    if (chargeManual) {
      next.chargeManual = true;
      if (
        section.manualHours !== undefined &&
        section.manualHours !== null &&
        String(section.manualHours).trim() !== ""
      ) {
        next.manualHours = toApiDecimalString(section.manualHours, 4);
      } else {
        delete next.manualHours;
      }
    } else {
      next.chargeManual = false;
      delete next.manualHours;
    }

    return next;
  };

  const sanitizeRepHoursSection = (sectionValue) => {
    const section = ensureObject(sectionValue);

    if (!shouldStripInvoiceOnlyFieldsForConfig) {
      return section;
    }

    const { 
      overtimeHours, 
      holidayHours, 
      weekdayNumberOfDays,
      weekendNumberOfDays,
      socialMediaWeekdayNumberOfDays,
      socialMediaWeekendNumberOfDays,
      actualAmHours,
      overageHours,
      ...allowedFields 
    } = section;
    return allowedFields;
  };

  const sanitizedLaborSections = Object.keys(payload).reduce((acc, sectionId) => {
    if (sectionId === "rep_hours_onshore" || sectionId === "rep_hours_offshore") {
      acc[sectionId] = sanitizeRepHoursSection(payload?.[sectionId]);
      return acc;
    }

    acc[sectionId] = sanitizeLaborTaskSection(sectionId, payload?.[sectionId]);
    return acc;
  }, {});

  return {
    ...payload,
    ...sanitizedLaborSections,
  };
};

const mergeLaborUiOnlyFields = (tabId, apiPayload, newPayload) => {
  if (tabId === "fulfilment") {
    const safeApiPayload = ensureObject(apiPayload);
    const originalNewPayload = ensureObject(newPayload);

    const apiFulfillment = ensureObject(safeApiPayload?.fulfillment);
    const newFulfillment = ensureObject(originalNewPayload?.fulfillment);

    return {
      ...safeApiPayload,
      ...(safeApiPayload?.fulfillment !== undefined || originalNewPayload?.fulfillment !== undefined
        ? {
            fulfillment: {
              ...apiFulfillment,
              ...(newFulfillment?.chargeBrandwise !== undefined
                ? { chargeBrandwise: Boolean(newFulfillment.chargeBrandwise) }
                : {}),
              ...(newFulfillment?.perPieceRateByBrand !== undefined
                ? { perPieceRateByBrand: newFulfillment.perPieceRateByBrand }
                : {}),
              ...(newFulfillment?.pieceTitleByBrand !== undefined
                ? { pieceTitleByBrand: newFulfillment.pieceTitleByBrand }
                : {}),
              ...(newFulfillment?.postageTitleByBrand !== undefined
                ? { postageTitleByBrand: newFulfillment.postageTitleByBrand }
                : {}),
              ...(newFulfillment?.program_brands !== undefined
                ? { program_brands: newFulfillment.program_brands }
                : {}),
              ...(newFulfillment?.postage_rows !== undefined
                ? { postage_rows: newFulfillment.postage_rows }
                : {}),
              ...(newFulfillment?.postage_data !== undefined
                ? { postage_data: newFulfillment.postage_data }
                : {}),
            },
          }
        : {}),
      ...(safeApiPayload?.fulfillment_misc === undefined &&
      originalNewPayload?.fulfillment_misc !== undefined
        ? { fulfillment_misc: originalNewPayload.fulfillment_misc }
        : {}),
      ...(safeApiPayload?.supplies === undefined && originalNewPayload?.supplies !== undefined
        ? { supplies: originalNewPayload.supplies }
        : {}),
      ...(safeApiPayload?.storage === undefined && originalNewPayload?.storage !== undefined
        ? { storage: originalNewPayload.storage }
        : {}),
      ...(safeApiPayload?.gift_cards === undefined && originalNewPayload?.gift_cards !== undefined
        ? { gift_cards: originalNewPayload.gift_cards }
        : {}),
      ...(safeApiPayload?.fedex_expenses === undefined &&
      originalNewPayload?.fedex_expenses !== undefined
        ? { fedex_expenses: originalNewPayload.fedex_expenses }
        : {}),
    };
  }

  if (tabId !== "labor") return ensureObject(apiPayload);

  const safeApiPayload = ensureObject(apiPayload);
  const originalNewPayload = ensureObject(newPayload);

  const mergeRepHoursSection = (sectionId) => {
    const apiSection = ensureObject(safeApiPayload?.[sectionId]);
    const newSection = ensureObject(originalNewPayload?.[sectionId]);

    return {
      ...apiSection,
      ...(newSection?.overtimeHours !== undefined
        ? { overtimeHours: newSection.overtimeHours }
        : {}),
      ...(newSection?.holidayHours !== undefined
        ? { holidayHours: newSection.holidayHours }
        : {}),
    };
  };

  const mergeTaskOverrideFields = (sectionId) => {
    const apiSection = ensureObject(safeApiPayload?.[sectionId]);
    const newSection = ensureObject(originalNewPayload?.[sectionId]);

    if (Object.keys(apiSection).length === 0 && Object.keys(newSection).length === 0) {
      return undefined;
    }

    return {
      ...apiSection,
      ...(newSection?.chargeManual !== undefined
        ? { chargeManual: Boolean(newSection.chargeManual) }
        : {}),
      ...(newSection?.manualHours !== undefined
        ? { manualHours: newSection.manualHours }
        : {}),
      ...(newSection?.actualAmHours !== undefined && apiSection.actualAmHours === undefined
        ? { actualAmHours: newSection.actualAmHours }
        : {}),
    };
  };

  const mergedTaskSections = Array.from(
    new Set([
      ...Object.keys(safeApiPayload),
      ...Object.keys(originalNewPayload),
    ])
  ).reduce((acc, sectionId) => {
    if (sectionId === "rep_hours_onshore" || sectionId === "rep_hours_offshore") {
      return acc;
    }

    const merged = mergeTaskOverrideFields(sectionId);
    if (merged !== undefined) {
      acc[sectionId] = merged;
    }
    return acc;
  }, {});

  return {
    ...safeApiPayload,
    ...mergedTaskSections,
    rep_hours_onshore: mergeRepHoursSection("rep_hours_onshore"),
    rep_hours_offshore: mergeRepHoursSection("rep_hours_offshore"),
  };
};

const validateLaborTaskManualHours = (payload = {}) => {
  const errors = {};

  Object.entries(ensureObject(payload)).forEach(([sectionId, sectionValue]) => {
    if (sectionId === "rep_hours_onshore" || sectionId === "rep_hours_offshore") {
      return;
    }

    if (!String(sectionId || "").startsWith("task_")) {
      return;
    }

    const section = ensureObject(sectionValue);
    const isFlatRate =
      section.isFlatRate === undefined
        ? true
        : toApiBoolean(section.isFlatRate, true);
    if (isFlatRate || !toApiBoolean(section.chargeManual, false)) {
      return;
    }

    const raw = section.manualHours;
    if (raw === undefined || raw === null || String(raw).trim() === "") {
      errors[`payload.sections.${sectionId}.manualHours`] = [
        "Manual Hours is required.",
      ];
      return;
    }

    const parsed = Number(String(raw).replace(/[$,%\s,]/g, ""));
    if (!Number.isFinite(parsed) || parsed < 0) {
      errors[`payload.sections.${sectionId}.manualHours`] = [
        "Manual Hours must be greater than or equal to 0.",
      ];
    }
  });

  return errors;
};

export const setActiveInvoiceMonth = (monthKey) => ({
  type: types.INVOICES_SET_ACTIVE_MONTH,
  monthKey,
});

export const setActiveInvoiceClient = (monthKey, clientId) => ({
  type: types.INVOICES_SET_ACTIVE_CLIENT,
  monthKey,
  clientId,
});

export const setActiveInvoiceTab = (clientId, tabId) => ({
  type: types.INVOICES_SET_ACTIVE_TAB,
  clientId,
  tabId,
});

export const setInvoiceTabNew = (clientId, tabId, payload) => ({
  type: types.INVOICES_SET_TAB_NEW,
  clientId,
  tabId,
  payload,
});

export const setInvoiceTabDirty = (clientId, tabId, isDirty = true) => ({
  type: types.INVOICES_SET_TAB_DIRTY,
  clientId,
  tabId,
  isDirty,
});

export const clearInvoiceTabValidationErrors = (clientId, tabId) => ({
  type: types.INVOICES_CLEAR_VALIDATION_ERROR,
  clientId,
  tabId,
});

export const fetchInvoiceMonthSummary =
  (params = {}, object_viewed_id = "") =>
  (dispatch) => {
    dispatch({ type: types.INVOICES_REQUEST });
    dispatch(showLoader("Loading invoices..."));

    return axios
      .get(`${API_URL}invoices`, {
        params: {
          ...params,
          view: params?.view || "month_summary",
          object_viewed_id,
        },
        headers: authHeaders(),
      })
      .then((res) => {
        dispatch(hideLoader());
        const envelope = normalizeEnvelope(res);

        dispatch({
          type: types.INVOICES_MONTH_SUMMARY_SUCCESS,
          monthSummary: extractArray(envelope?.data),
          meta: envelope?.meta,
        });

        return Promise.resolve(envelope);
      })
      .catch((error) => {
        dispatch(hideLoader());
        const failure = handleApiFailure(error, dispatch, "Unable to load invoice months");
        dispatch({ type: types.INVOICES_FAILURE, error: failure.message });
        return Promise.reject(error);
      });
  };

export const fetchInvoiceMonthClients =
  ({ monthKey, params = {}, silent = false }, object_viewed_id = "") =>
  (dispatch) => {
    if (!silent) {
      dispatch({ type: types.INVOICES_REQUEST });
    }

    const resolvedView = params?.view || "month_programs";
    const resolvedAll =
      params?.all !== undefined
        ? params.all
        : resolvedView === "month_programs"
          ? true
          : undefined;

    return axios
      .get(`${API_URL}invoices`, {
        params: {
          ...params,
          month_key: monthKey,
          view: resolvedView,
          ...(resolvedAll !== undefined ? { all: resolvedAll } : {}),
          object_viewed_id,
        },
        headers: authHeaders(),
      })
      .then((res) => {
        const envelope = normalizeEnvelope(res);
        dispatch({
          type: types.INVOICES_MONTH_CLIENTS_SUCCESS,
          monthKey,
          clients: extractArray(envelope?.data),
        });
        return Promise.resolve(envelope);
      })
      .catch((error) => {
        const failure = handleApiFailure(error, dispatch, "Unable to load invoice programs");
        dispatch({ type: types.INVOICES_FAILURE, error: failure.message });
        return Promise.reject(error);
      });
  };

export const fetchInvoiceClientDetail =
  (
    {
      monthKey,
      programId,
      params = {},
      silent = false,
      showCommonLoader = false,
    },
    object_viewed_id = ""
  ) =>
  (dispatch) => {
    if (!silent) {
      dispatch({ type: types.INVOICES_REQUEST });
    }

    if (showCommonLoader) {
      dispatch(showLoader("Loading invoice..."));
    }

    return axios
      .get(`${API_URL}invoices`, {
        params: {
          ...params,
          month_key: monthKey,
          program_id: programId,
          view: params?.view || "program_detail",
          object_viewed_id,
        },
        headers: authHeaders(),
      })
      .then((res) => {
        if (showCommonLoader) {
          dispatch(hideLoader());
        }

        const envelope = normalizeEnvelope(res);
        const record = extractRecord(envelope?.data);

        dispatch({
          type: types.INVOICES_CLIENT_DETAIL_SUCCESS,
          clientId: programId,
          record,
          tabNew: normalizeTabNewFromRecord(record),
          dirtyTabs: createDirtyMap(false),
        });

        return Promise.resolve(envelope);
      })
      .catch((error) => {
        if (showCommonLoader) {
          dispatch(hideLoader());
        }

        const failure = handleApiFailure(error, dispatch, "Unable to load invoice program detail");
        dispatch({ type: types.INVOICES_FAILURE, error: failure.message });
        return Promise.reject(error);
      });
  };

export const saveInvoiceClientTab =
  (
    {
      invoiceId,
      monthKey,
      clientId,
      tabId,
      payload,
      selectedSectionIds = [],
      allSectionIds = null,
      params = {},
      shouldSyncConfig = true,
      configProgramId = null,
    },
    object_viewed_id = ""
  ) =>
  (dispatch, getState) => {
    let normalizedPayload = ensureObject(payload);

    if (tabId === "fulfilment" && Array.isArray(selectedSectionIds)) {
      normalizedPayload = selectedSectionIds.reduce((acc, sectionId) => {
        const sourceValue = payload?.[sectionId];

        if (
          sectionId === "fulfillment_misc" ||
          sectionId === "supplies" ||
          sectionId === "storage" ||
          sectionId === "gift_cards" ||
          sectionId === "fedex_expenses"
        ) {
          acc[sectionId] = Array.isArray(sourceValue)
            ? sourceValue
            : sourceValue && typeof sourceValue === "object"
              ? [sourceValue]
              : [];
          return acc;
        }

        if (
          sourceValue &&
          typeof sourceValue === "object" &&
          !Array.isArray(sourceValue)
        ) {
          acc[sectionId] = sourceValue;
        } else {
          acc[sectionId] = {};
        }

        return acc;
      }, {});
    }

    const payloadForSave = sanitizeInvoiceTabPayload(tabId, normalizedPayload, {
      forConfigSync: false,
      monthKey,
    });
    const payloadForSync = sanitizeInvoiceTabPayload(tabId, normalizedPayload, {
      forConfigSync: true,
      monthKey,
    });

    if (tabId === "labor") {
      const laborValidationErrors = validateLaborTaskManualHours(payloadForSave);
      if (Object.keys(laborValidationErrors).length > 0) {
        dispatch({
          type: types.INVOICES_VALIDATION_ERROR,
          clientId,
          tabId,
          errors: laborValidationErrors,
        });
        dispatch({
          type: types.INVOICES_SET_TAB_SAVING,
          clientId,
          tabId,
          saving: false,
        });
        return Promise.reject(
          new Error("Validation failed. Please review the highlighted fields.")
        );
      }
    }
    const sectionStates = buildSectionStatesForTab(
      tabId,
      selectedSectionIds,
      allSectionIds,
    );

    dispatch({
      type: types.INVOICES_SET_TAB_SAVING,
      clientId,
      tabId,
      saving: true,
    });

    dispatch(clearInvoiceTabValidationErrors(clientId, tabId));

    const resolvedInvoiceId =
      invoiceId ||
      getState()?.invoicesState?.clientInvoiceById?.[clientId]?.id ||
      getState()?.invoicesState?.clientInvoiceById?.[clientId]?.invoice_id ||
      null;

    if (!resolvedInvoiceId) {
      const error = new Error("Invoice id is missing");
      dispatch(showSnackbar("Unable to save: invoice id is missing for this program"));
      dispatch({
        type: types.INVOICES_SET_TAB_SAVING,
        clientId,
        tabId,
        saving: false,
      });
      return Promise.reject(error);
    }

    return axios
      .patch(
        `${API_URL}invoices/${resolvedInvoiceId}/tabs/${tabId}`,
        {
          month_key: monthKey,
          program_id: clientId,
          tab: tabId,
          payload: {
            sections: payloadForSave,
            ...(sectionStates.length > 0 && { section_states: sectionStates }),
            ...(params?.subtotal_amount !== undefined && { subtotal_amount: params.subtotal_amount }),
          },
          ...params,
        },
        {
          params: { object_viewed_id },
          headers: authHeaders(),
        }
      )
      .then((res) => {
        const envelope = normalizeEnvelope(res);
        const record = extractRecord(envelope?.data);
        const invoiceSections = getInvoiceSectionsFromRecord(record, monthKey);
        const persistedTabPayloadFromApi =
          pickFirstNonEmptyObject(
            record?.sections,
            record?.payload?.sections,
            record?.tabs?.[tabId]?.sections,
            payloadForSave
          );

        const persistedTabPayload = mergeLaborUiOnlyFields(
          tabId,
          persistedTabPayloadFromApi,
          normalizedPayload
        );

        const normalizedRecord = record
          ? {
              ...record,
              tabs: {
                ...ensureObject(record?.tabs),
                ...(tabId !== "invoice" && sectionStates.length > 0
                  ? {
                      [tabId]: {
                        ...ensureObject(record?.tabs?.[tabId]),
                        sections: persistedTabPayload,
                        section_states: sectionStates,
                      },
                    }
                  : {}),
                invoice: {
                  ...ensureObject(record?.tabs?.invoice),
                  sections: invoiceSections,
                },
              },
            }
          : null;

        dispatch({
          type: types.INVOICES_TAB_SAVE_SUCCESS,
          clientId,
          tabId,
          payload: persistedTabPayload,
          record: normalizedRecord,
          invoicePayload: invoiceSections,
        });

        dispatch(showSnackbar(envelope?.message || "Invoice tab saved successfully", "success"));

        if (shouldSyncConfig) {
          dispatch(
            syncInvoiceTabWithConfiguration(
              {
                tabId,
                payload: payloadForSync,
                programId: configProgramId,
                sectionStates,
              },
              object_viewed_id
            )
          ).catch(() => {
            // Sync action already handles snackbar/error state; avoid unhandled promise rejection.
          });
        }

        return Promise.resolve(envelope);
      })
      .catch((error) => {
        const failure = handleApiFailure(error, dispatch, "Unable to save invoice tab");
        if (failure.validationErrors) {
          dispatch({
            type: types.INVOICES_VALIDATION_ERROR,
            clientId,
            tabId,
            errors: failure.validationErrors,
          });
        }

        dispatch({
          type: types.INVOICES_SET_TAB_SAVING,
          clientId,
          tabId,
          saving: false,
        });

        return Promise.reject(error);
      });
  };

export const syncInvoiceTabWithConfiguration =
  (
    {
      tabId,
      payload,
      programId = null,
      sectionStates = [],
      message = "",
    },
    object_viewed_id = ""
  ) =>
  (dispatch, getState) => {
    const body = {
      tab: tabId,
      payload: {
        sections: payload,
      },
      program_id: programId,
    };

    dispatch({
      type: types.INVOICES_SYNC_STATUS,
      status: "syncing",
      message: "Syncing invoice tab with configuration...",
      updatedAt: Date.now(),
    });

    return axios
      .patch(`${API_URL}invoice-configurations/sync-from-invoice`, body, {
        params: { object_viewed_id },
        headers: authHeaders(),
      })
      .then((res) => {
        const envelope = normalizeEnvelope(res);
        const incomingRecord = extractRecord(envelope?.data);
        const configurationId =
          incomingRecord?.id ||
          getState()?.invoiceConfigurationState?.invoiceConfigurationRecord?.id ||
          null;

        if (incomingRecord) {
          dispatch({
            type: "INVOICE_CONFIG_GET_SUCCESS",
            record: incomingRecord,
            actionType: "update",
          });
        } else {
          const existingRecord = getState()?.invoiceConfigurationState?.invoiceConfigurationRecord;
          if (existingRecord) {
            dispatch({
              type: "INVOICE_CONFIG_GET_SUCCESS",
              record: mergeTabIntoConfigRecord(existingRecord, tabId, payload),
              actionType: "update",
            });
          }
        }

        if (configurationId && Array.isArray(sectionStates) && sectionStates.length > 0) {
          return axios
            .patch(
              `${API_URL}invoice-configurations/${configurationId}/sections`,
              {
                section_states: sectionStates,
              },
              {
                params: { object_viewed_id },
                headers: authHeaders(),
              }
            )
            .then(() => envelope);
        }

        return envelope;
      })
      .then((envelope) => {

        dispatch({
          type: types.INVOICES_SYNC_CONFIG_RATES,
          message: message || envelope?.message || "Invoice and configuration are in sync",
        });

        return Promise.resolve(envelope);
      })
      .catch((error) => {
        const failure = handleApiFailure(
          error,
          dispatch,
          "Unable to sync invoice tab with invoice configuration"
        );

        dispatch({
          type: types.INVOICES_SYNC_STATUS,
          status: "error",
          message: failure.message,
          updatedAt: Date.now(),
        });

        return Promise.reject(error);
      });
  };

export const generateMonthlyInvoices =
  ({ monthKey }, object_viewed_id = "") =>
  (dispatch) => {
    return axios
      .post(
        `${API_URL}invoices/generate`,
        {
          month_key: monthKey,
        },
        {
          params: { object_viewed_id },
          headers: authHeaders(),
        }
      )
      .then((res) => {
        const envelope = normalizeEnvelope(res);
        dispatch(
          showSnackbar(
            envelope?.message || "Invoice publishing started for this month",
            "success"
          )
        );
        return Promise.resolve(envelope);
      })
      .catch((error) => {
        const failure = handleApiFailure(
          error,
          dispatch,
          "Unable to start invoice publishing"
        );
        dispatch(showSnackbar(failure?.message || "Unable to start invoice publishing"));
        return Promise.reject(error);
      });
  };

const transitionInvoiceClientStatus =
  ({ invoiceId, monthKey, clientId, transition, successMessage }, object_viewed_id = "") =>
  (dispatch, getState) => {
    const resolvedInvoiceId =
      invoiceId ||
      getState()?.invoicesState?.clientInvoiceById?.[clientId]?.id ||
      getState()?.invoicesState?.clientInvoiceById?.[clientId]?.invoice_id ||
      null;

    if (!resolvedInvoiceId) {
      const error = new Error("Invoice id is missing");
      dispatch(showSnackbar("Unable to update invoice status: invoice id is missing for this program"));
      return Promise.reject(error);
    }

    const resolvePublishStatus = (record = {}) =>
      String(
        record?.status ||
          record?.invoice_status ||
          record?.publish_status ||
          record?.state ||
          ""
      )
        .trim()
        .toLowerCase() || undefined;

    const resolveProgramStatus = (record = {}) => {
      if (
        !Object.prototype.hasOwnProperty.call(record, "program_status") &&
        !Object.prototype.hasOwnProperty.call(record, "programStatus")
      ) {
        return undefined;
      }

      const raw = record?.program_status ?? record?.programStatus ?? null;
      return raw === null || raw === undefined || raw === "" ? null : String(raw).trim().toLowerCase();
    };

    return axios
      .post(
        `${API_URL}invoices/${resolvedInvoiceId}/${transition}`,
        {
          month_key: monthKey,
          program_id: clientId,
        },
        {
          params: { object_viewed_id },
          headers: authHeaders(),
        }
      )
      .then((res) => {
        const envelope = normalizeEnvelope(res);
        const record = extractRecord(envelope?.data);
        const publishStatus = resolvePublishStatus(record);
        const programStatus = resolveProgramStatus(record);

        if (publishStatus !== undefined || programStatus !== undefined) {
          dispatch({
            type: types.INVOICES_CLIENT_STATUS_UPDATE,
            clientId,
            monthKey,
            ...(publishStatus !== undefined ? { publishStatus } : {}),
            ...(programStatus !== undefined ? { programStatus } : {}),
          });
        }

        dispatch(showSnackbar(envelope?.message || successMessage, "success"));
        return Promise.resolve(envelope);
      })
      .catch((error) => {
        const fallbackMessages = {
          unpublish: "Unable to unpublish invoice",
          republish: "Unable to republish invoice",
          working: "Unable to update invoice status to Working",
          ready_to_review: "Unable to update invoice status to Ready to Review",
        };
        const fallbackMessage =
          fallbackMessages[transition] || "Unable to update invoice status";
        const failure = handleApiFailure(error, dispatch, fallbackMessage);
        dispatch(showSnackbar(failure?.message || fallbackMessage));
        return Promise.reject(error);
      });
  };

export const updateInvoiceClientWorkflowStatus =
  ({ invoiceId, monthKey, clientId, workflowStatus }, object_viewed_id = "") =>
  transitionInvoiceClientStatus(
    {
      invoiceId,
      monthKey,
      clientId,
      transition: workflowStatus,
      successMessage:
        workflowStatus === "ready_to_review"
          ? "Invoice status updated to Ready to Review"
          : workflowStatus === "working"
            ? "Invoice status updated to Working"
            : "Invoice status updated",
    },
    object_viewed_id
  );

export const unpublishInvoiceClient =
  ({ invoiceId, monthKey, clientId }, object_viewed_id = "") =>
  transitionInvoiceClientStatus(
    {
      invoiceId,
      monthKey,
      clientId,
      transition: "unpublish",
      successMessage: "Invoice unpublished successfully",
    },
    object_viewed_id
  );

export const republishInvoiceClient =
  ({ invoiceId, monthKey, clientId }, object_viewed_id = "") =>
  transitionInvoiceClientStatus(
    {
      invoiceId,
      monthKey,
      clientId,
      transition: "republish",
      successMessage: "Invoice republished successfully",
    },
    object_viewed_id
  );

export const downloadInvoiceMonthZip =
  ({ monthKey }, object_viewed_id = "") =>
  (dispatch) => {
    dispatch(showLoader("Downloading invoices..."));

    return axios
      .get(`${API_URL}invoices/download`, {
        params: {
          month_key: monthKey,
          object_viewed_id,
        },
        headers: authHeaders(),
        responseType: "blob",
      })
      .then((res) => {
        const blob = new Blob([res?.data], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `invoices-${monthKey}.zip`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        dispatch(showSnackbar("Invoice ZIP download started", "success"));
        return Promise.resolve(res);
      })
      .catch((error) => {
        handleApiFailure(error, dispatch, "Unable to download invoice ZIP");
        return Promise.reject(error);
      })
      .finally(() => {
        dispatch(hideLoader());
      });
  };
